import { App } from 'antd';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { clientConfig } from 'src/api/axios/config';
import { FanguardApiContractsUserUserDetailModel } from 'src/api/generated';

import { get, patch, post, put, remove } from '@/api/api';
import { useAuth } from '@/contexts/AuthContext';
import { Schema as UserSchema } from '@/pages/Users/UsersNew/config';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useMutationWithMessages, buildQueryParams } from './helpers';
import { PagedResponse } from 'src/types/GlobalTypes';

const URI = 'users';

const useGetCurrentUser = () => {
  const { username } = useAuth();

  return useQuery({
    queryKey: [username],
    queryFn: async () => {
      const response = await get<FanguardApiContractsUserUserDetailModel>({ uri: `${URI}/${username}` });

      return response.data;
    },
    enabled: !!username,
  });
};

const useGetUser = (id: string | undefined) => {
  return useQuery({
    queryKey: [URI, id],
    queryFn: async () => {
      const response = await get<FanguardApiContractsUserUserDetailModel>({ uri: `${URI}/${id}` });

      return response.data;
    },
    enabled: !!id,
  });
};

interface useGetUsersParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
}

const useGetUsers = ({ page = 1, pageSize = 25, searchTerm = '' }: useGetUsersParams) => {
  return useQuery({
    queryKey: [URI, page, pageSize, searchTerm],
    queryFn: async () => {
      const response = await get<PagedResponse<FanguardApiContractsUserUserDetailModel>>({
        uri: `${URI}?${buildQueryParams({ page, pageSize, searchTerm })}`,
      });
      return response.data;
    },
  });
};

// redirect to user?
const useCreateUser = () => {
  return useMutationWithMessages<AxiosResponse<FanguardApiContractsUserUserDetailModel>, Error, UserSchema>(
    (data) => post({ uri: `${URI}`, data }),
    {
      action: 'create',
      entityType: 'user',
      queryKeysToInvalidate: [URI],
      customErrorMessage: true,
    },
  );
};

const useUpdateUser = (username: string | undefined | null) => {
  return useMutationWithMessages<AxiosResponse<FanguardApiContractsUserUserDetailModel>, Error, UserSchema>(
    (data) => patch({ uri: `${URI}/${username}`, data }),
    {
      action: 'update',
      entityType: 'user',
      queryKeysToInvalidate: [URI],
    },
  );
};

const useDeleteUser = () => {
  return useMutationWithMessages<AxiosResponse<FanguardApiContractsUserUserDetailModel>, Error, string>(
    (id: string) => remove({ uri: `${URI}/${id}` }),
    {
      action: 'delete',
      entityType: 'user',
      queryKeysToInvalidate: [URI],
    },
  );
};

const useResetUserPassword = () => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const data = {
    client: clientConfig.getClient()?.length > 0 ? clientConfig.getClient() : 'system',
  };

  return useMutation<AxiosResponse<FanguardApiContractsUserUserDetailModel>, Error, string>({
    mutationFn: async (id: string) =>
      post({ uri: `${URI}/${id}/invitations/resetpassword`, data, headers: { client: data.client } }),
    onSuccess: () => {
      message.success(`${t('users.table.resetPasswordSuccess')}`);
    },
    onError: () => {
      message.error(`${t('users.table.resetPasswordError')}`);
    },
  });
};

const useUnlockUser = (options?: { invalidateAll?: boolean }) => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useMutation<AxiosResponse<FanguardApiContractsUserUserDetailModel>, Error, string>({
    mutationFn: async (username: string) => post({ uri: `${URI}/${username}/unlock` }),
    onSuccess: (_, username) => {
      message.success(`${t('users.unlockUserSuccess')}`);
      if (options?.invalidateAll) {
        queryClient.invalidateQueries({ queryKey: [URI] });
      } else {
        queryClient.invalidateQueries({ queryKey: [URI, username] });
      }
    },
    onError: () => {
      message.error(`${t('users.unlockUserError')}`);
    },
  });
};

const useSetUserPassword = () => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  return useMutation<
    AxiosResponse<FanguardApiContractsUserUserDetailModel>,
    Error,
    { username: string; password: string }
  >({
    mutationFn: async ({ username, password }: { username: string; password: string }) =>
      put({
        uri: `${URI}/${username}/set-password`,
        data: {
          password,
          client: clientConfig.getClient()?.length > 0 ? clientConfig.getClient() : 'system',
        },
      }),
    onSuccess: () => {
      message.success(`${t('login.changeUserPasswordSuccess')}`);
    },
    onError: () => {
      message.error(`${t('login.changeUserPasswordError')}`);
    },
  });
};

export {
  useCreateUser,
  useDeleteUser,
  useGetCurrentUser,
  useGetUser,
  useGetUsers,
  useResetUserPassword,
  useSetUserPassword,
  useUnlockUser,
  useUpdateUser,
};
